export type AppCommandGroup =
  | "selection"
  | "current-page"
  | "create"
  | "navigate"
  | "other"

export type AppCommandScope = "global" | "page" | "selection" | "dialog"

export type AppCommandKind =
  | "action"
  | "back"
  | "create"
  | "export"
  | "navigate"
  | "open"
  | "search"
  | "toggle"

export interface AppCommand {
  action: () => void | Promise<void>
  aliases?: string[]
  available?: boolean
  description?: string
  group?: AppCommandGroup
  id: string
  isBranch?: boolean
  key?: string
  kind?: AppCommandKind
  keywords?: string[]
  label: string
  parentId?: string | null
  searchOnly?: boolean
  scope?: AppCommandScope
}

export interface ResolvedAppCommand extends AppCommand {
  group: AppCommandGroup
  kind: AppCommandKind
  scope: AppCommandScope
  source: "contextual" | "entity" | "navigation" | "shortcut" | "system"
}

function startsWithAnyPrefix(value: string, prefixes: string[]) {
  return prefixes.some((prefix) => value.startsWith(prefix))
}

function resolveCommandKind(command: Partial<AppCommand>): AppCommandKind {
  if (command.kind) {
    return command.kind
  }

  const label = command.label?.toLowerCase() ?? ""
  const key = command.key?.toLowerCase() ?? ""

  if (command.group === "navigate") {
    return "navigate"
  }

  if (key === "/" || label.includes("search")) {
    return "search"
  }

  if (key === "enter" || startsWithAnyPrefix(label, ["open ", "inspect "])) {
    return "open"
  }

  if (
    key === "n" ||
    startsWithAnyPrefix(label, ["create ", "new ", "generate "])
  ) {
    return "create"
  }

  if (
    key === "b" ||
    label === "go back" ||
    startsWithAnyPrefix(label, ["back ", "close ", "clear "])
  ) {
    return "back"
  }

  if (
    label.includes("download") ||
    label.includes("export") ||
    startsWithAnyPrefix(label, ["copy "])
  ) {
    return "export"
  }

  if (startsWithAnyPrefix(label, ["switch ", "toggle ", "show ", "hide "])) {
    return "toggle"
  }

  return "action"
}

function resolveCommandScope(command: Partial<AppCommand>): AppCommandScope {
  if (command.scope) {
    return command.scope
  }

  if (command.group === "navigate") {
    return "global"
  }

  if (command.group === "selection") {
    return "selection"
  }

  return "page"
}

function resolveCommandGroup(command: Partial<AppCommand>): AppCommandGroup {
  if (command.group) {
    return command.group
  }

  const scope = resolveCommandScope(command)
  const kind = resolveCommandKind(command)

  if (scope === "selection") {
    return "selection"
  }

  if (kind === "create") {
    return "create"
  }

  if (scope === "global") {
    return "navigate"
  }

  return "current-page"
}

export function resolveAppCommand(
  command: AppCommand,
  source: ResolvedAppCommand["source"],
): ResolvedAppCommand {
  return {
    ...command,
    group: resolveCommandGroup(command),
    kind: resolveCommandKind(command),
    scope: resolveCommandScope(command),
    source,
  }
}
